<template>
  <AuthCard>
    <p>
      Enter the code from your authenticator app, or one of your recovery codes.
    </p>

    <form @submit.prevent="onSubmit">
      <div v-if="formAlertMessages.length" class="c-login__error">
        <Alert
          v-for="(msg, i) in formAlertMessages"
          :key="i"
          :type="msg.type"
          @close="formAlertMessages.splice(i, 1)"
        >
          {{ msg.message }}
        </Alert>
      </div>

      <div class="c-login__input">
        <TextInput
          name="code"
          label="Authentication code"
          autocomplete="one-time-code"
          placeholder="Authentication code"
          hide-label
          required
          autofocus
          :model-value="form.code"
          :errors="getFieldErrors('code')"
          data-test="two-factor-code"
          @update:model-value="form.code = $event"
        />
      </div>
      <button
        type="submit"
        class="btn btn-lg btn-primary mt-4 w-100 rounded-1"
        :disabled="submitting || !form.code"
        data-test="two-factor-submit"
      >
        {{ submitting ? 'Verifying…' : 'Continue' }}
      </button>
      <div class="text-center mt-3">
        <router-link :to="{name: 'signin'}" data-test="two-factor-back-to-signin">
          <small>Back to Sign In</small>
        </router-link>
      </div>
    </form>
  </AuthCard>
</template>

<script setup>
import {inject, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import * as Sentry from '@sentry/browser';
import AuthCard from './components/AuthCard.vue';
import Alert from '@canopyllc/roots/components/Alert.vue';
import TextInput from '@canopyllc/roots/components/forms/TextInput.vue';
import {useErrorManager} from '@canopyllc/roots/composables/errorManager.js';

const api = inject('api'),
      urls = inject('urls'),
      router = useRouter(),

      form = reactive({code: ''}),
      submitting = ref(false),

      {
        formAlertMessages, getFieldErrors, clearFormErrors, setNonFieldErrors, handleAllauthErrors,
      } = useErrorManager();

async function onSubmit() {
  clearFormErrors();
  submitting.value = true;
  try {
    await api.post('/_allauth/browser/v1/auth/2fa/authenticate', {code: form.code});
    window.location.href = urls.nextUrl || '/';
  } catch (err) {
    const status = err?.response?.status;
    if (status === 409) {
      // Already fully authenticated — nothing left to challenge.
      window.location.href = urls.nextUrl || '/';
      return;
    }
    if (status === 410) {
      // The login flow expired; send the user back to start over.
      router.push({name: 'signin'});
      return;
    }
    if (!handleAllauthErrors(err)) {
      Sentry.captureException(err);
      setNonFieldErrors([{message: 'That code was not valid. Please try again.', type: 'danger'}]);
    }
    submitting.value = false;
  }
}
</script>
